import { useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * Renders its children only once the placeholder scrolls into view (and keeps
 * them mounted after). Used to defer a lazy chunk — the embedded chart's Recharts
 * bundle — so a page whose reader never scrolls to the chart never downloads it.
 * Without IntersectionObserver (or SSR), it renders eagerly.
 */
export function WhenVisible({
  children,
  minHeight = 240,
  rootMargin = '200px',
}: {
  children: ReactNode;
  minHeight?: number;
  rootMargin?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(() => typeof IntersectionObserver === 'undefined');

  useEffect(() => {
    if (visible) return;
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [visible, rootMargin]);

  return <div ref={ref}>{visible ? children : <div style={{ minHeight }} aria-hidden />}</div>;
}
